import { useState } from "react"
import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"

import type { NavigatorStore } from "@/stores/navigator-store"

import { ContextSidebarSearch } from "@/features/chat/components/context-sidebar-search"
import { ActiveCitations } from "@/features/navigator/components/active-citations/active-citations"
import { CategoryFilter } from "@/features/navigator/components/category-filter"
import { useLibrariesSelection } from "@/features/navigator/hooks/use-libraries-selection"
import { ProjectEmbeddingIndicator } from "@/features/projects/components/project-embedding-indicator"
import { ProjectSettingsDialog } from "@/features/projects/components/project-settings-dialog"
import { useProject } from "@/hooks/use-projects"
import { useNavigatorStore } from "@/stores/navigator-store"

import { Separator } from "../../../components/ui/separator"
import { SidebarContent, SidebarMenu, SidebarMenuItem } from "../../../components/ui/sidebar"

type BaseSettingsSidebarProps = {
  sessionType: string
  projectId?: string
  renderContextContent?: (searchQuery: string) => ReactNode
}

export function BaseSettingsSidebar({
  sessionType,
  projectId,
  renderContextContent,
}: BaseSettingsSidebarProps) {
  const { t } = useTranslation("", { keyPrefix: "project_settings.files" })
  const [settingsOpen, setSettingsOpen] = useState(false)
  const { data: project } = useProject(projectId, sessionType)
  const [contextSearchQuery, setContextSearchQuery] = useState("")
  const navigatorStore = useNavigatorStore(sessionType)
  const citationCount = navigatorStore((state: NavigatorStore) => state.citations.length)
  const { hasLibraryTool, showCategoryPicker, restrictedLibraryId } =
    useLibrariesSelection(sessionType)
  const shouldShowCategoryFilter =
    hasLibraryTool && showCategoryPicker && Boolean(restrictedLibraryId)
  const shouldShowContextSearch = Boolean(renderContextContent) || citationCount > 0

  return (
    <>
      <SidebarContent className="flex flex-col gap-4 overflow-hidden p-2">
        {projectId && (
          <>
            <div className="flex flex-col gap-3">
              <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                {t("project_files")}
              </span>
              <ProjectEmbeddingIndicator
                projectId={projectId}
                onClick={() => setSettingsOpen(true)}
              />
            </div>
            <Separator />
          </>
        )}
        {shouldShowCategoryFilter && (
          <>
            <CategoryFilter libraryId={restrictedLibraryId!} sessionType={sessionType} />
            <Separator />
          </>
        )}
        {shouldShowContextSearch && (
          <ContextSidebarSearch value={contextSearchQuery} onChange={setContextSearchQuery} />
        )}
        {renderContextContent?.(contextSearchQuery)}
        <SidebarMenu className="flex h-full min-h-0 flex-1">
          <SidebarMenuItem className="flex h-full min-h-0 flex-1 flex-col">
            <ActiveCitations
              hideSearchInput={shouldShowContextSearch}
              searchQuery={contextSearchQuery}
              sessionType={sessionType}
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarContent>
      {projectId && (
        <ProjectSettingsDialog
          defaultSection="files"
          open={settingsOpen}
          project={project}
          projectId={projectId}
          sessionType={sessionType}
          onOpenChange={setSettingsOpen}
        />
      )}
    </>
  )
}
